'use client';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { EllipsisVertical, CircleDot } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useQueryState } from 'nuqs';
import type { ColumnDef } from '@/lib/table-features';

import { useDataTable } from '@/hooks/use-data-table';
import { useHashParamDialog } from '@/hooks/use-hash-dialog';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { STORAGE_LOCATIONS_HASH } from '../constants';
import { useStorageLocations } from '../services/storage-locations.queries';
import { useDeleteStorageLocation } from '../services/storage-locations.mutations';
import { StorageLocationEditDialog } from './storage-location-edit-dialog';
import { StorageLocationCard } from './storage-location-card';
import { StorageLocationCompactRow } from './storage-location-compact-row';
import type { StorageLocation } from '../types';

const getCodeColor = (code: string) => {
  const colors = [
    'bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
    'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
    'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
    'bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
    'bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300',
    'bg-cyan-100 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300'
  ];
  const hash = code.charCodeAt(0) + (code.charCodeAt(1) || 0);
  return colors[hash % colors.length];
};

export function StorageLocationsListing() {
  const t = useTranslations();
  const [page, setPage] = useQueryState('page', { defaultValue: '1' });
  const [perPage, _setPerPage] = useQueryState('per_page', {
    defaultValue: '10'
  });
  const [search, setSearch] = useQueryState('search', { defaultValue: '' });
  // Overlay open-state lives in the URL hash (#edit/<id>, #delete/<id>) so
  // overlays are deep-linkable and close on browser Back. Destructure the
  // stable callbacks for use in hook dep arrays (React Compiler rule).
  const {
    open: editOpen,
    param: editId,
    openWith: openEdit,
    onOpenChange: closeEdit
  } = useHashParamDialog(STORAGE_LOCATIONS_HASH.edit);
  const {
    open: deleteOpen,
    param: deleteId,
    openWith: openDelete,
    onOpenChange: closeDelete
  } = useHashParamDialog(STORAGE_LOCATIONS_HASH.delete);
  const [view, setView] = useDataView('storage-locations');

  const queryParams = useMemo(
    () => ({
      page: parseInt(page),
      per_page: parseInt(perPage),
      search: search || undefined
    }),
    [page, perPage, search]
  );

  const {
    data: response,
    isLoading,
    isPlaceholderData
  } = useStorageLocations(queryParams);
  const storageLocations = response?.data ?? [];
  const pagination = response?.pagination;

  // Resolve the hash id param back to the loaded row (row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay).
  const editTarget = editOpen
    ? (storageLocations.find((l) => String(l.id) === editId) ?? null)
    : null;
  const deleteTargetId =
    deleteOpen && deleteId !== null ? Number(deleteId) : null;

  const deleteStorageLocation = useDeleteStorageLocation();

  const handleDelete = async (id: number) => {
    try {
      await deleteStorageLocation.mutateAsync({ id });
      closeDelete(false);
    } catch {
      // Error handled by mutation
    }
  };

  const handleEdit = useCallback(
    (item: StorageLocation) => openEdit(String(item.id)),
    [openEdit]
  );

  const handleDeleteClick = useCallback(
    (item: StorageLocation) => openDelete(String(item.id)),
    [openDelete]
  );

  const columns: ColumnDef<StorageLocation>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('storageLocations.columns.locationName')}
          />
        ),
        cell: ({ row }) => {
          const location = row.original;
          return (
            <div className='flex items-start gap-3'>
              <div
                className={cn(
                  'rounded px-2 py-1 text-xs font-bold whitespace-nowrap',
                  getCodeColor(location.code)
                )}
              >
                {location.code}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='font-semibold'>{location.name}</p>
                {location.breadcrumb && (
                  <p className='text-muted-foreground text-xs uppercase'>
                    {location.breadcrumb}
                  </p>
                )}
              </div>
            </div>
          );
        }
      },
      {
        id: 'capacity',
        accessorKey: 'capacity',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('storageLocations.columns.shelves')}
          />
        ),
        cell: ({ getValue }) => <span>{getValue() as React.ReactNode}</span>
      },
      {
        id: 'utilization',
        accessorKey: 'utilization',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('storageLocations.columns.utilization')}
          />
        ),
        cell: ({ getValue }) => {
          const util = getValue() as number;
          return (
            <div className='w-32 space-y-1'>
              <Progress value={util} className='h-2' />
              <p className='text-muted-foreground text-xs'>{util}%</p>
            </div>
          );
        }
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t('storageLocations.columns.status')}
          />
        ),
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const label =
            status === 'active'
              ? t('storageLocations.statusActive')
              : t('storageLocations.statusFull');
          const color =
            status === 'active'
              ? 'text-green-600 dark:text-green-400'
              : 'text-red-600 dark:text-red-400';
          return (
            <div className='flex items-center gap-2'>
              <CircleDot className={cn('h-3 w-3 fill-current', color)} />
              <span className='text-sm'>{label}</span>
            </div>
          );
        }
      },
      {
        id: 'actions',
        header: t('storageLocations.columns.actions'),
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm' className='h-8 w-8 p-0'>
                <EllipsisVertical className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() => openEdit(String(row.original.id))}
              >
                {t('storageLocations.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => openDelete(String(row.original.id))}
              >
                {t('storageLocations.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      }
    ],
    [t, openEdit, openDelete]
  );

  const { table } = useDataTable({
    data: storageLocations,
    columns,
    pageKey: 'storage-locations',
    pageCount: pagination?.totalPages ?? 0
  });

  const tableState = table.state;

  const sortOptions = useMemo(
    () => [
      {
        label: t('storageLocations.columns.locationName'),
        value: 'name'
      },
      {
        label: t('storageLocations.columns.shelves'),
        value: 'capacity'
      },
      {
        label: t('storageLocations.columns.utilization'),
        value: 'utilization'
      },
      {
        label: t('storageLocations.columns.status'),
        value: 'status'
      }
    ],
    [t]
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
        <Input
          placeholder={t('storageLocations.searchPlaceholder')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage('1');
          }}
          className='max-w-sm'
        />
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='storage-locations'
          />
        </div>
      </div>

      {isLoading ? (
        <ListingSkeleton view={view} />
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {storageLocations.length === 0 ? (
              <div className='text-muted-foreground col-span-full py-8 text-center'>
                {t('storageLocations.noStorageLocations')}
              </div>
            ) : (
              storageLocations.map((location) => (
                <StorageLocationCard
                  key={location.id}
                  location={location}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))
            )}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {storageLocations.length === 0 ? (
              <div className='text-muted-foreground py-8 text-center'>
                {t('storageLocations.noStorageLocations')}
              </div>
            ) : (
              storageLocations.map((location) => (
                <StorageLocationCompactRow
                  key={location.id}
                  location={location}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                />
              ))
            )}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable table={table}>
            {storageLocations.length === 0 && !isLoading && (
              <div className='text-muted-foreground py-8 text-center'>
                {t('storageLocations.noStorageLocations')}
              </div>
            )}
          </DataTable>
        </div>
      )}

      <StorageLocationEditDialog
        storageLocation={editOpen ? editTarget : null}
        onOpenChange={closeEdit}
      />

      <AlertDialog open={deleteOpen} onOpenChange={closeDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('storageLocations.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('storageLocations.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-3'>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTargetId && handleDelete(deleteTargetId)}
              disabled={deleteStorageLocation.isPending}
              className='bg-destructive hover:bg-destructive/90'
            >
              {deleteStorageLocation.isPending
                ? 'Deleting...'
                : t('storageLocations.delete')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
